import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Card } from "../UI/Card.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { OnboardingLiveListPeriod } from "../gql/graphql.ts";
import { useOnboardingLives } from "./useOnboardingLives.ts";
import { formatSlot } from "./onboardingLiveTime.ts";
import { onboardingLiveDetailPathOf } from "./OnboardingLivePaths.ts";

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className={"rounded-lg border p-3"}>
      <p className={"text-xs text-slate-500"}>{label}</p>
      <p className={"text-xl font-bold"}>{value}</p>
    </div>
  );
}

export function TeacherOnboardingLivesCard({
  teacherId,
}: {
  teacherId: string;
}) {
  const navigate = useNavigate();
  const { onboardingLives: upcoming, loading: loadingUpcoming } =
    useOnboardingLives({
      period: OnboardingLiveListPeriod.Upcoming,
      teacherId,
    });
  const { onboardingLives: past, loading: loadingPast } = useOnboardingLives({
    period: OnboardingLiveListPeriod.Past,
    teacherId,
  });
  if ((loadingUpcoming || loadingPast) && upcoming.length + past.length <= 0)
    return (
      <Card>
        <LoadingRows />
      </Card>
    );
  if (upcoming.length + past.length <= 0) return null;

  const last90Days = past.filter((l) =>
    dayjs(l.startUtc).isAfter(dayjs().subtract(90, "days")),
  );
  const withFeedback = past.filter((l) => !!l.feedback);
  const attendees = withFeedback.reduce(
    (total, l) => total + (l.feedback?.participantsCount ?? 0),
    0,
  );

  return (
    <Card>
      <div className={"space-y-4"}>
        <div>
          <h2 className={"font-bold text-lg"}>Live onboarding</h2>
          <p className={"text-sm text-slate-500"}>
            Le live onboarding non rientrano nelle statistiche, nel tier e nel
            cap settimanale delle classi tradizionali.
          </p>
        </div>
        <div className={"grid grid-cols-2 md:grid-cols-4 gap-3"}>
          <Stat
            label={"Condotte (ultimi 90 giorni)"}
            value={last90Days.length}
          />
          <Stat label={"Condotte in totale"} value={past.length} />
          <Stat label={"Future"} value={upcoming.length} />
          <Stat
            label={"Feedback inviati"}
            value={`${withFeedback.length} / ${past.length}`}
          />
          <Stat label={"Partecipanti dichiarati"} value={attendees} />
        </div>
        {upcoming.length > 0 ? (
          <div>
            <p className={"font-semibold text-sm mb-2"}>Prossime live</p>
            <ul className={"divide-y text-sm"}>
              {upcoming.slice(0, 10).map((l) => (
                <li
                  key={l.id}
                  onClick={() => navigate(onboardingLiveDetailPathOf(l.id))}
                  className={
                    "py-2 cursor-pointer hover:bg-slate-50 flex justify-between"
                  }
                >
                  <span>{formatSlot(l.startUtc)}</span>
                  <span className={"text-slate-500"}>
                    {l.participantsCount} iscritti
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
